import React, { createContext, useState, useEffect, useContext, useCallback } from 'react'
import ApiService from '../services/ApiService'
import { useSearchAccesses } from './SearchAccessesContext'
import UserContext from './UserContext'
import { useTenant } from './TenantContext'

interface Profile {
  id: number
  name: string
  admin: boolean
  permissions: string[]
  createdAt: string
  updatedAt: string
}

interface ProfileContextProps {
  profiles: Profile[]
  setProfiles: React.Dispatch<React.SetStateAction<Profile[]>>
  fetchProfiles: () => Promise<void>
  newProfiles: number
  setNewProfiles: React.Dispatch<React.SetStateAction<number>>
  deletedProfile: number
  setDeletedProfile: React.Dispatch<React.SetStateAction<number>>
  loadQuantity: number
  setLoadQuantity: React.Dispatch<React.SetStateAction<number>>
  usersLoadQuantity: number
  setUsersLoadQuantity: React.Dispatch<React.SetStateAction<number>>
  fetchProfilesWithoutAdmin: () => Promise<void>
  hasMoreProfiles: boolean
  setNewFunctions?: React.Dispatch<React.SetStateAction<number>>
  totalProfiles: number | null
}

interface ProfileProviderProps {
  children?: React.ReactNode
}

const ProfileContext = createContext<ProfileContextProps | undefined>(undefined)

export const ProfileProvider: React.FC<ProfileProviderProps> = ({ children }) => {
  const [profiles, setProfiles] = useState<Profile[]>([])
  const [newProfiles, setNewProfiles] = useState(0)
  const [deletedProfile, setDeletedProfile] = useState(0)
  const [newFunctions, setNewFunctions] = useState(0)
  const { searchText } = useSearchAccesses()
  const [loadQuantity, setLoadQuantity] = useState(10)
  const [usersLoadQuantity, setUsersLoadQuantity] = useState(10)
  const [hasMoreProfiles, setHasMoreProfiles] = useState(true)
  const [totalProfiles, setTotalProfiles] = useState<number | null>(null)
  const { user } = useContext(UserContext)
  const { tenantId } = useTenant()
  const MAX_PROFILES_FETCH = 1000

  const normalizeProfiles = (rawData: any): Profile[] => {
    if (Array.isArray(rawData)) {
      return rawData
    }
    if (Array.isArray(rawData?.data)) {
      return rawData.data
    }
    return []
  }

  const fetchProfiles = async () => {
    if (tenantId === null) {
      console.warn('Tenant ausente, aguardando tenant válido antes de buscar perfis')
      return
    }

    try {
      const response = await ApiService.profiles.findAll({ 
        filter: searchText, 
        limit: loadQuantity,
        offset: 0
      })
      const rawData = response.data
      const profilesData = normalizeProfiles(rawData)
      const filteredProfiles = profilesData.filter(profile => profile.name.toUpperCase() !== 'ADM')

      setProfiles(filteredProfiles)
      setHasMoreProfiles(filteredProfiles.length >= loadQuantity)
      
      try {
        const totalResp = await ApiService.profiles.getTotal({ filter: searchText })
        const total = totalResp?.data?.total || totalResp?.data || filteredProfiles.length
        setTotalProfiles(total)
      } catch (e) {
        setTotalProfiles(filteredProfiles.length)
      }
    } catch (error) {
      console.error('Erro ao trazer os perfis', error)
    }
  }

  const fetchProfilesWithoutAdmin = useCallback(async () => {
    if (tenantId === null) {
      console.warn('Tenant ausente, aguardando tenant válido antes de buscar perfis')
      return
    }

    try {
      const response = await ApiService.profiles.findAll({
        filter: searchText,
        limit: loadQuantity,
        offset: 0
      })
      const rawData = response.data
      const profilesData = normalizeProfiles(rawData)
      const filteredProfiles = profilesData.filter((profile: Profile) => profile.name.toUpperCase() !== 'ADM')

      setProfiles(filteredProfiles)
      setHasMoreProfiles(filteredProfiles.length >= loadQuantity)
      
      try {
        const totalResp = await ApiService.profiles.getTotal({ filter: searchText })
        const total = totalResp?.data?.total || totalResp?.data || filteredProfiles.length
        setTotalProfiles(total)
      } catch (e) {
        setTotalProfiles(filteredProfiles.length)
      }
    } catch (error) {
      console.error('Erro ao trazer os perfis', error)
      setHasMoreProfiles(false)
    }
  }, [searchText, loadQuantity, tenantId])

  useEffect(() => {
    if (user && user.id && tenantId !== null) {
      fetchProfilesWithoutAdmin()
    }
  }, [loadQuantity, searchText, fetchProfilesWithoutAdmin, user, tenantId, newProfiles, deletedProfile])

  return (
    <ProfileContext.Provider value={{ profiles, setProfiles, fetchProfiles, newProfiles, setNewProfiles, deletedProfile, setDeletedProfile, loadQuantity, setLoadQuantity, usersLoadQuantity, setUsersLoadQuantity, fetchProfilesWithoutAdmin, hasMoreProfiles, setNewFunctions, totalProfiles }}>
      {children}
    </ProfileContext.Provider>
  )
}

export const useProfileContext = () => {
  const context = useContext(ProfileContext)
  if (!context) {
    throw new Error('useProfileContext deve ser usado dentro de um ProfileProvider')
  }
  return context
}
